import { useState } from "react";
import { Radar, RefreshCw, Share2 } from "lucide-react";
import { KeyDiscoveries } from "./KeyDiscoveries";
import { LinkedInCarouselGenerator } from "../carousel/LinkedInCarouselGenerator";

const InsightsDisplay = ({ insights }) => {
  if (insights.flashcards) {
    return (
      <div className="space-y-6">
        {insights.headline && (
          <h3 className="text-2xl font-bold text-center bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
            {insights.headline}
          </h3>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {insights.flashcards?.map((card, idx) => (
            <div key={idx} className="bg-muted/30 border border-border rounded-xl p-5">
              <div className="text-3xl font-black mb-2 text-violet-400">{card.stat}</div>
              <h4 className="font-semibold">{card.title}</h4>
              <p className="text-sm text-muted-foreground">{card.insight}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <pre className="text-sm text-muted-foreground overflow-auto">{JSON.stringify(insights, null, 2)}</pre>
  );
};

export const InsightsTab = ({ insights, insightsLoading, generateInsights, stats, researchStats, onShowInfographic }) => {
  const [query, setQuery] = useState("");
  const [showCarousel, setShowCarousel] = useState(false);
  
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Gender Gap Infographic Banner */}
      <button
        onClick={onShowInfographic}
        data-testid="open-infographic-btn"
        className="w-full group relative overflow-hidden rounded-xl p-6 text-left transition-all hover:scale-[1.005]"
        style={{ background: "linear-gradient(135deg, rgba(236,72,153,0.12) 0%, rgba(59,130,246,0.08) 100%)", border: "1px solid rgba(236,72,153,0.2)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
              <span className="text-xs font-mono uppercase tracking-wider text-pink-400/80">New</span>
            </div>
            <h3 className="text-lg font-bold text-slate-100">AI Gender Gap Infographic</h3>
            <p className="text-sm text-slate-400 mt-1">Shareable visual summary: download, tweet, or post to LinkedIn</p>
          </div>
          <Share2 className="w-6 h-6 text-pink-400/60 group-hover:text-pink-400 transition-colors" />
        </div>
      </button>

      <KeyDiscoveries />

      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Radar className="w-5 h-5 text-primary" />
          AI-Powered Insights
        </h2>
        <p className="text-muted-foreground mb-4">Generate insights from the dataset using AI analysis. Ask questions or get summary statistics.</p>
        <div className="flex gap-3">
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Ask a question about the data... (or leave blank for overview)" className="flex-1 px-4 py-2 bg-background border border-border rounded-lg" data-testid="insights-query" />
          <button onClick={() => generateInsights(query)} disabled={insightsLoading} data-testid="generate-insights-btn"
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
            {insightsLoading ? (<><RefreshCw className="w-4 h-4 animate-spin" />Analyzing...</>) : (<><Radar className="w-4 h-4" />Generate</>)}
          </button>
        </div>
      </div>

      {insights && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Generated Insights</h3>
            <button onClick={() => setShowCarousel(!showCarousel)}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-all"
              data-testid="toggle-carousel-btn">
              <Share2 className="w-4 h-4" />{showCarousel ? "Hide Carousel" : "Create LinkedIn Carousel"}
            </button>
          </div>
          <InsightsDisplay insights={insights} />
        </div>
      )}

      {insights && showCarousel && (
        <div className="bg-card border border-border rounded-lg p-6">
          <LinkedInCarouselGenerator stats={stats} researchStats={researchStats} preloadedInsights={insights} />
        </div>
      )}

      {!insights && !insightsLoading && (
        <div className="text-center py-12">
          <Radar className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-muted-foreground">Click "Generate" to create AI-powered insights</p>
          <p className="text-sm text-muted-foreground mt-2">Based on {stats?.total_apps || 0} apps in the database</p>
        </div>
      )}
    </div>
  );
};
